'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  ShieldCheck,
  Building2,
  Calendar,
  User,
  Hash,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  ArrowLeft,
  Clock,
  XCircle,
  PauseCircle,
  FileText,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type CredentialStatus = 'Active' | 'Suspended' | 'Revoked' | 'Expired';

const CERT_ID = 'ERT-CERT-DEMO-SUPPLY-001';

const STATUS_CFG: Record<
  CredentialStatus,
  {
    label: string;
    color: string;
    bg: string;
    border: string;
    icon: React.ReactNode;
    headline: string;
    sub: string;
  }
> = {
  Active: {
    label: 'Active',
    color: '#16a34a',
    bg: 'rgba(22,163,74,0.08)',
    border: 'rgba(22,163,74,0.28)',
    icon: <CheckCircle2 size={20} />,
    headline: 'Credential Status: Active',
    sub: 'This credential has been confirmed by the issuing institution and is currently active.',
  },
  Suspended: {
    label: 'Suspended',
    color: '#d97706',
    bg: 'rgba(217,119,6,0.08)',
    border: 'rgba(217,119,6,0.28)',
    icon: <PauseCircle size={20} />,
    headline: 'Credential Status: Suspended',
    sub: 'This credential has been temporarily suspended by the issuing institution.',
  },
  Revoked: {
    label: 'Revoked',
    color: '#dc2626',
    bg: 'rgba(220,38,38,0.08)',
    border: 'rgba(220,38,38,0.28)',
    icon: <XCircle size={20} />,
    headline: 'Credential Status: Revoked',
    sub: 'This credential has been revoked by the issuing institution.',
  },
  Expired: {
    label: 'Expired',
    color: '#64748b',
    bg: 'rgba(100,116,139,0.08)',
    border: 'rgba(100,116,139,0.22)',
    icon: <Clock size={20} />,
    headline: 'Credential Status: Expired',
    sub: 'This credential has passed its validity period and is no longer active.',
  },
};

const STATUS_FIELDS = [
  { icon: <Hash size={14} />, label: 'Certificate ID', value: CERT_ID, mono: true },
  { icon: <FileText size={14} />, label: 'Record Title', value: 'Test Supplier Status Credential' },
  { icon: <User size={14} />, label: 'Credential Holder', value: 'Designated Test Supplier' },
  { icon: <Building2 size={14} />, label: 'Issuing Institution', value: 'Designated Test Issuer' },
  { icon: <FileText size={14} />, label: 'Credential Type', value: 'Supplier-style credential demo' },
  { icon: <Calendar size={14} />, label: 'Issue Date', value: 'June 1, 2026' },
];

export default function TrustLedgerVerifyPage() {
  const [status, setStatus] = useState<CredentialStatus>('Active');
  const cfg = STATUS_CFG[status];

  return (
    <div
      className="min-h-screen"
      style={{ background: '#0a1628', fontFamily: 'var(--font-dm-sans, sans-serif)' }}
    >
      {/* Top bar */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{
          background: 'rgba(10,22,40,0.97)',
          backdropFilter: 'blur(12px)',
          borderColor: 'rgba(255,255,255,0.07)',
        }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: '#e9a84c', opacity: 0.65 }}
        />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link
            href="/"
            className="flex items-center gap-2 text-sm font-medium flex-shrink-0"
            style={{ color: 'rgba(232,237,243,0.55)' }}
          >
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">Back to Ertzyx</span>
          </Link>
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={26} />
            <span className="font-bold text-white text-sm">Ertzyx</span>
          </Link>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold flex-shrink-0"
            style={{
              background: 'rgba(233,168,76,0.1)',
              border: '1px solid rgba(233,168,76,0.3)',
              color: '#e9a84c',
            }}
          >
            <ShieldCheck size={12} />
            <span>Status Check</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        {/* Demo status toggle */}
        <div
          className="rounded-xl border px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-3"
          style={{
            borderColor: 'rgba(255,255,255,0.07)',
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          <span
            className="text-xs font-bold uppercase tracking-wider w-full sm:w-auto"
            style={{ color: 'rgba(232,237,243,0.4)' }}
          >
            Demo: preview status
          </span>
          {(['Active', 'Suspended', 'Revoked', 'Expired'] as CredentialStatus[]).map((s) => {
            const c = STATUS_CFG[s];
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className="text-xs font-bold px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: status === s ? c.bg : 'transparent',
                  border: `1px solid ${status === s ? c.border : 'rgba(255,255,255,0.1)'}`,
                  color: status === s ? c.color : 'rgba(232,237,243,0.4)',
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Main status card */}
        <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#fff' }}>
          {/* Status header */}
          <div
            className="px-6 sm:px-8 py-6"
            style={{ background: cfg.bg, borderBottom: `2px solid ${cfg.border}` }}
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: cfg.bg, border: `2px solid ${cfg.border}` }}
              >
                <span style={{ color: cfg.color }}>{cfg.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-black text-slate-900 mb-1">{cfg.headline}</h1>
                <p className="text-sm text-slate-600 leading-relaxed">{cfg.sub}</p>
              </div>
              <span
                className="text-sm font-black px-4 py-2 rounded-xl flex-shrink-0"
                style={{
                  background: cfg.bg,
                  color: cfg.color,
                  border: `1.5px solid ${cfg.border}`,
                }}
              >
                {cfg.label}
              </span>
            </div>
          </div>

          {/* Title row */}
          <div className="px-6 sm:px-8 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                <AppLogo size={32} />
              </div>
              <div>
                <p className="font-black text-slate-900 text-base leading-snug">
                  Test Supplier Status Credential
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Supplier-style credential demo &middot; Pre-commercial demonstration
                </p>
              </div>
            </div>
          </div>

          {/* Key fields */}
          <div className="px-6 sm:px-8 py-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {STATUS_FIELDS.map((f) => (
                <div key={f.label} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-slate-400">{f.icon}</span>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {f.label}
                    </span>
                  </div>
                  <p
                    className="font-semibold text-slate-800 leading-snug"
                    style={{ fontFamily: f.mono ? 'monospace' : undefined, fontSize: f.mono ? 12 : 14 }}
                  >
                    {f.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Short disclaimer */}
            <div
              className="flex items-start gap-2 rounded-xl border px-4 py-3 mb-5"
              style={{
                borderColor: 'rgba(233,168,76,0.25)',
                background: 'rgba(233,168,76,0.05)',
              }}
            >
              <AlertTriangle size={13} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
              <p className="text-xs leading-relaxed" style={{ color: '#78350f' }}>
                <span className="font-bold">Pre-commercial demonstration.</span> This is a simulated
                record used to demonstrate the Ertzyx Trust Ledger workflow. It does not represent
                official supplier eligibility, procurement standing, or any government credential.
                No live government data is connected.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href="/verify?id=ERT-CERT-DEMO-SUPPLY-001"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #1B3A6B, #254E93)' }}
              >
                <ShieldCheck size={15} />
                View Full Verification
              </Link>
              <Link
                href="/certificate/rec-demo-supplier"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
              >
                <ExternalLink size={15} />
                View Certificate
              </Link>
            </div>
          </div>

          {/* Footer */}
          <div
            className="px-6 sm:px-8 py-4 border-t border-slate-100"
            style={{ background: '#fafafa' }}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-slate-400 leading-relaxed">
                Verification reflects Ertzyx Trust Ledger status only. Not an official credential.
              </p>
              <Link href="/" className="flex items-center gap-2 flex-shrink-0">
                <AppLogo size={22} />
                <span className="text-xs font-bold text-slate-500">Ertzyx</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
